import { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import {
  commonDataGridStyles,
  enhancedDataGridStyles,
  compactDataGridStyles,
  defaultDataGridProps,
  purchasesDataGridProps,
} from '../styles/dataGridStyles';

export type DataGridVariant = 'common' | 'enhanced' | 'compact';
export type DataGridType = 'default' | 'purchases';

interface IUseDataGridStylesProps {
  variant?: DataGridVariant;
  type?: DataGridType;
  customStyles?: SxProps<Theme>;
}

/**
 * Hook to get consistent DataGrid styles and props across the application
 */
export const useDataGridStyles = ({
  variant = 'common',
  type = 'default',
  customStyles,
}: IUseDataGridStylesProps = {}) => {
  const styles = useMemo(() => {
    let baseStyles: SxProps<Theme>;

    switch (variant) {
      case 'enhanced':
        baseStyles = enhancedDataGridStyles;
        break;
      case 'compact':
        baseStyles = compactDataGridStyles;
        break;
      default:
        baseStyles = commonDataGridStyles;
        break;
    }

    // Merge with custom styles if provided
    if (customStyles) {
      return [baseStyles, customStyles];
    }

    return baseStyles;
  }, [variant, customStyles]);

  const props = useMemo(() => {
    switch (type) {
      case 'purchases':
        return purchasesDataGridProps;
      default:
        return defaultDataGridProps;
    }
  }, [type]);

  return {
    styles,
    props,
  };
};

// Usage example:
// const { styles, props } = useDataGridStyles({ 
//   variant: 'enhanced', 
//   type: 'purchases',
//   customStyles: { height: 500 }
// });
// 
// <DataGrid 
//   sx={styles}
//   {...props}
//   // your other props
// />